import axios, { AxiosInstance, AxiosResponse } from 'axios'
import type { Session } from '../types'

// Types
export interface User {
  id: number
  email: string
  name: string
  role: string
  created_at: string
  updated_at: string
}

export interface License {
  id: number
  license_key: string
  user_id: number
  plan_id: number
  plan_name: string
  status: 'active' | 'expired' | 'cancelled'
  valid_from: number // timestamp
  valid_to: number // timestamp
  max_devices: number
  active_devices: number
  created_at: number // timestamp
  updated_at: number // timestamp
  price?: number
  features_json?: any
}

export interface Device {
  id: number
  license_id: number
  device_id: string
  device_name: string
  device_type: 'windows' | 'mac' | 'linux'
  last_active: string
  is_active: boolean
  created_at: string
}

export interface UserActivity {
  user_id: number
  user_email: string
  user_name: string
  last_heartbeat: string | null
  ip_address: string | null
  device_info: string | null
  license_status: 'active' | 'blocked' | 'suspended' | 'trial'
  online_status: 'online' | 'offline' | 'idle'
  session_duration: number | null
  force_logout_pending: boolean
}


export interface Payment {
  id: number
  user_id: number
  amount: number
  currency: string
  status: string
  payment_method: string
  created_at: string
}

export interface SubscriptionPlan {
  id: number
  name: string
  price_monthly: number
  price_yearly: number
  max_devices: number
  features: string
  is_active: boolean
}

// API configuration
const API_BASE_URL = 'https://panel.presentclic.com/api'

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken')
      if (refreshToken && !error.config._retry) {
        error.config._retry = true
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken
          })
          const { token } = response.data
          localStorage.setItem('token', token)
          
          error.config.headers.Authorization = `Bearer ${token}`
          return api.request(error.config)
        } catch (refreshError) {
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          window.location.href = '/panel-clic/login'
        }
      } else {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        if (window.location.pathname !== '/panel-clic/login') {
          window.location.href = '/panel-clic/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }): Promise<AxiosResponse<{ token: string; refreshToken: string; user: User }>> =>
    api.post('/auth/login', credentials),
    
  register: (userData: { name: string; email: string; password: string }): Promise<AxiosResponse<{ token: string; refreshToken: string; user: User }>> =>
    api.post('/auth/register', userData),
    
  logout: (): Promise<AxiosResponse<void>> =>
    api.post('/auth/logout'),
    
  verifyToken: (): Promise<AxiosResponse<{ valid: boolean; user: User }>> =>
    api.get('/auth/verify'),
    
  refreshToken: (refreshToken: string): Promise<AxiosResponse<{ token: string }>> =>
    api.post('/auth/refresh', { refreshToken }),

  forgotPassword: (email: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/reset-password', { token, newPassword })
}

// User API
export const userAPI = {
  getProfile: (): Promise<AxiosResponse<User>> =>
    api.get('/users/profile'),
    
  updateProfile: (data: Partial<User>): Promise<AxiosResponse<User>> =>
    api.put('/users/profile', data),
    
  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<AxiosResponse<void>> =>
    api.put('/users/change-password', data),
    
  getBillingInfo: (): Promise<AxiosResponse<any>> =>
    api.get('/users/billing-info'),
    
  updateBillingInfo: (data: any): Promise<AxiosResponse<any>> =>
    api.post('/users/billing-info', data)
}

// License API
export const licenseAPI = {
  getMyLicenses: (): Promise<AxiosResponse<License[]>> =>
    api.get('/licenses/my-licenses'),
    
  getLicenseDevices: (licenseId: number): Promise<AxiosResponse<{ devices: Device[] }>> =>
    api.get(`/licenses/${licenseId}/devices`),
    
  deactivateDevice: (deviceId: number): Promise<AxiosResponse<void>> =>
    api.put(`/licenses/device/${deviceId}/deactivate`),
    
  verifyLicense: (data: { licenseKey: string; deviceId: string; deviceName: string }): Promise<AxiosResponse<any>> =>
    api.post('/licenses/verify', data),
    
  createLicense: (data: { userId: number; planId: number; durationDays?: number }): Promise<AxiosResponse<any>> =>
    api.post('/licenses/create', data)
}

// Device API  
export const deviceAPI = {
  getMyDevices: (): Promise<AxiosResponse<Device[]>> =>
    api.get('/devices/my-devices'),
    
  removeDevice: (deviceId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/devices/${deviceId}`)
}

// Session API
export const sessionAPI = {
  getMySessions: (): Promise<AxiosResponse<Session[]>> =>
    api.get('/sessions/my-sessions'),
    
  createSession: (data: { name: string; description?: string; session_code?: string; device_limit?: number }): Promise<AxiosResponse<Session>> =>
    api.post('/sessions/create', data),
    
  deleteSession: (sessionId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/sessions/${sessionId}`),
    
  getSessionByCode: (code: string): Promise<AxiosResponse<Session>> =>
    api.get(`/sessions/${code}`),
    
  joinSession: (code: string): Promise<AxiosResponse<void>> =>
    api.post(`/sessions/${code}/join`),
    
  sendSessionEmail: (sessionId: number, email: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post(`/sessions/${sessionId}/send-email`, { email })
}

// Payment API
export const paymentAPI = {
  getPaymentHistory: (): Promise<AxiosResponse<Payment[]>> =>
    api.get('/payments/history'),
    
  createPayment: (data: { planId: number; paymentMethod: string }): Promise<AxiosResponse<{ paymentUrl: string }>> =>
    api.post('/payments/create', data)
}

// Stripe API
export const stripeAPI = {
  test: (): Promise<AxiosResponse<any>> =>
    api.get('/stripe/test'),
    
  testEnv: (): Promise<AxiosResponse<any>> =>
    api.get('/stripe/test-env'),
    
  createCheckoutSession: (data: { planId?: number; billingPeriod?: string }): Promise<AxiosResponse<{
    success: boolean;
    sessionResult?: {
      success: boolean;
      sessionId: string;
      sessionUrl: string;
      planName: string;
      amount: string;
    };
    sessionError?: string;
  }>> =>
    api.post('/stripe/create-checkout-session', data),
    
  cancelSubscription: (subscriptionId: string | number): Promise<AxiosResponse<{
    success: boolean;
    message: string;
  }>> =>
    api.post('/stripe/cancel-subscription', { subscriptionId })
}

// Plan API
export const planAPI = {
  getPlans: (): Promise<AxiosResponse<SubscriptionPlan[]>> =>
    api.get('/plans')
}

// Admin API - uses different base URL
const adminApi: AxiosInstance = axios.create({
  baseURL: 'https://panel.presentclic.com/api/admin',
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
})

// Admin API request interceptor
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Admin API response interceptor
adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refreshToken')
      if (refreshToken && !error.config._retry) {
        error.config._retry = true
        try {
          const response = await axios.post(`https://panel.presentclic.com/api/auth/refresh`, {
            refreshToken
          })
          const { token } = response.data
          localStorage.setItem('token', token)
          
          error.config.headers.Authorization = `Bearer ${token}`
          return adminApi.request(error.config)
        } catch (refreshError) {
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          window.location.href = '/panel-clic/login'
        }
      } else {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        if (window.location.pathname !== '/panel-clic/login') {
          window.location.href = '/panel-clic/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

export const adminAPI = {
  // Dashboard stats
  getStats: (): Promise<AxiosResponse<any>> =>
    adminApi.get('/stats'),
    
  // User management
  getUsers: (): Promise<AxiosResponse<User[]>> =>
    adminApi.get('/users'),
    
  createUser: (userData: { name: string; email: string; password: string; role?: string; plan_id?: number; duration_days?: number }): Promise<AxiosResponse<any>> =>
    adminApi.post('/users/create', userData),
    
  updateUser: (userId: number, data: Partial<User>): Promise<AxiosResponse<User>> =>
    adminApi.put(`/users/${userId}`, data),
    
  deleteUser: (userId: number): Promise<AxiosResponse<void>> =>
    adminApi.delete(`/users/${userId}`),
    
  // License management
  getLicenses: (filters?: { plan_id?: string; status?: string; user_email?: string }): Promise<AxiosResponse<License[]>> => {
    const params = new URLSearchParams()
    if (filters?.plan_id) params.append('plan_id', filters.plan_id)
    if (filters?.status) params.append('status', filters.status) 
    if (filters?.user_email) params.append('user_email', filters.user_email)
    
    return adminApi.get(`/licenses?${params.toString()}`)
  },
  
  deleteLicense: (licenseId: number): Promise<AxiosResponse<void>> =>
    adminApi.delete(`/licenses/${licenseId}`),
    
  updateLicenseStatus: (licenseId: number, status: string): Promise<AxiosResponse<any>> =>
    adminApi.patch(`/licenses/${licenseId}/status`, { status }),
    
  extendLicense: (licenseId: number, days: number): Promise<AxiosResponse<any>> =>
    adminApi.post(`/licenses/${licenseId}/extend`, { days }),
    
  // Email configuration
  getEmailConfig: (): Promise<AxiosResponse<any>> =>
    adminApi.get('/email-settings'),
    
  updateEmailConfig: (config: any): Promise<AxiosResponse<any>> =>
    adminApi.put('/email-settings', config),
    
  // Company data
  getCompanyData: (): Promise<AxiosResponse<any>> =>
    adminApi.get('/company-data'),
    
  getPublicCompanyData: (): Promise<AxiosResponse<any>> =>
    api.get('/public/company-data'),
    
  updateCompanyData: (data: any): Promise<AxiosResponse<any>> =>
    adminApi.put('/company-data', data),
    
  uploadLogo: (formData: FormData): Promise<AxiosResponse<any>> =>
    adminApi.post('/company-data/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // Security functions
  getUserActivities: (): Promise<AxiosResponse<any>> =>
    adminApi.get('/security/activities'),
    
  forceLogout: (userId: number): Promise<AxiosResponse<any>> =>
    adminApi.post(`/security/force-logout/${userId}`),
    
  blockUser: (userId: number, reason: string): Promise<AxiosResponse<any>> =>
    adminApi.post(`/security/block-user/${userId}`, { reason }),
    
  unblockUser: (userId: number): Promise<AxiosResponse<any>> =>
    adminApi.post(`/security/unblock-user/${userId}`),
    
  // Payments
  getPayments: (): Promise<AxiosResponse<Payment[]>> =>
    adminApi.get('/payments'),
    
  refundPayment: (paymentId: number): Promise<AxiosResponse<any>> =>
    adminApi.post(`/payments/${paymentId}/refund`)
}

export default api